import { useState, useEffect, useCallback } from 'react';
import { masterSupabase } from '../lib/supabase-master.js';

// Hook für Voice Consent Management
export const useVoiceConsent = (userId) => {
  const [consentStatus, setConsentStatus] = useState(null); // null, true, false
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // Lade bestehenden Consent-Status
  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    loadConsentStatus();
  }, [userId]);

  const loadConsentStatus = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await masterSupabase
        .from('user_consents')
        .select('consent_given, consent_timestamp')
        .eq('user_id', userId)
        .eq('consent_type', 'voice_recognition')
        .order('consent_timestamp', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error loading consent status:', error);
        // Bei Fehler: Modal anzeigen für neue Entscheidung
        setConsentStatus(null);
        setShowModal(true);
        return;
      }

      if (data && data.length > 0) {
        // Bestehender Consent gefunden
        setConsentStatus(data[0].consent_given);
        setShowModal(false);
      } else {
        // Kein Consent gefunden - Modal anzeigen
        setConsentStatus(null);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error in loadConsentStatus:', error);
      setConsentStatus(null);
      setShowModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Speichere Consent-Entscheidung
  const saveConsent = async (consentGiven) => {
    if (!userId) {
      throw new Error('User ID required');
    }

    try {
      // Sammle Browser-Informationen für rechtssichere Dokumentation
      const userAgent = navigator.userAgent;
      const timestamp = new Date().toISOString();
      
      // IP-Adresse wird server-seitig erfasst
      const consentData = {
        user_id: userId,
        consent_type: 'voice_recognition',
        consent_given: consentGiven,
        consent_timestamp: timestamp,
        user_agent: userAgent,
        consent_version: 'v1.0' // Für zukünftige AGB-Änderungen
      };

      const { error } = await masterSupabase
        .from('user_consents')
        .insert([consentData]);

      if (error) {
        console.error('Error saving consent:', error);
        throw error;
      }

      // Update lokalen Status
      setConsentStatus(consentGiven);
      setShowModal(false);

      console.log(`✅ Voice consent ${consentGiven ? 'granted' : 'declined'} and saved`);
      
      return true;
    } catch (error) {
      console.error('Error in saveConsent:', error);
      throw error;
    }
  };

  // Handle Accept
  const handleAccept = useCallback(async () => {
    try {
      await saveConsent(true);
      
      // Zusätzlich: Mikrofon-Berechtigung anfordern
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop()); // Sofort stoppen, nur Berechtigung testen
        console.log('🎤 Microphone permission granted');
      } catch (micError) {
        console.warn('Microphone permission denied:', micError);
        // Consent trotzdem speichern - User kann später Berechtigung erteilen
      }
      
    } catch (error) {
      console.error('Error accepting consent:', error);
      throw error;
    }
  }, [userId]);

  // Handle Decline
  const handleDecline = useCallback(async () => {
    try {
      await saveConsent(false);
    } catch (error) {
      console.error('Error declining consent:', error);
      throw error;
    }
  }, [userId]);

  // Consent widerrufen (für Einstellungen)
  const revokeConsent = useCallback(async () => {
    try {
      await saveConsent(false);
    } catch (error) {
      console.error('Error revoking consent:', error);
      throw error;
    }
  }, [userId]);

  // Consent erneuern (für Einstellungen)
  const renewConsent = useCallback(async () => {
    try {
      await saveConsent(true);
    } catch (error) {
      console.error('Error renewing consent:', error);
      throw error;
    }
  }, [userId]);

  // Modal manuell anzeigen (für Einstellungen)
  const showConsentModal = useCallback(() => {
    setShowModal(true);
  }, []);

  return {
    // Status
    consentStatus, // null = unbekannt, true = zugestimmt, false = abgelehnt
    isLoading,
    showModal,
    
    // Actions
    handleAccept,
    handleDecline,
    revokeConsent,
    renewConsent,
    showConsentModal,
    
    // Modal Control
    closeModal: () => setShowModal(false),
    
    // Utility
    hasVoicePermission: consentStatus === true,
    needsConsent: consentStatus === null,
    consentDeclined: consentStatus === false
  };
};

